import { Component, OnInit, Output, EventEmitter, Input, ViewChild } from '@angular/core';
import { DataElement, FileUpload, FileValidationParams, FileValidationResponse, newCatSystemVersion, Product } from 'src/app/shared/models/common';
import { NgRedux } from '@redux/redux-compatibility.service';
import { AppState } from 'src/app/shared/redux/store';
import { RevService } from 'src/app/shared/services/rev.service';
import { RevCatalog, RevDoc } from 'src/app/shared/models/rev.model';
import { UploadService } from 'src/app/shared/services/upload.service';
import { SharedService } from 'src/app/shared/services/shared.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CatService } from 'src/app/shared/services/cat.service';
import { DocMetadataHandler } from 'src/app/add-doc-metadata/services/add-doc-metadata.interface';
import { VersionDocMetadataHandler } from 'src/app/add-doc-metadata/services/add-doc-metadata-version.interface';
import { AddDocMetadataComponent } from 'src/app/add-doc-metadata/add-doc-metadata.component';


@Component({
    selector: 'add-rev-doc',
    templateUrl: './add-rev-doc.component.html',
    styleUrls: ['./add-rev-doc.component.scss'],
  providers: [
    // Both handlers are provided; the component picks the right one in ngOnInit:
    // - "Add document" uses the base DocMetadataHandler
    // - "Add version" uses VersionDocMetadataHandler, which inherits the version tree's
    //   metadata and renders it read-only.
    DocMetadataHandler,
    VersionDocMetadataHandler
  ],
    standalone: false
})
export class AddRevDocComponent implements OnInit {
  @Input() parentDoc: RevDoc;
  @Output() onClose = new EventEmitter();
  @Output() onDocCreated = new EventEmitter<RevDoc>();
  @ViewChild('addDocMetadataComponent') addDocMetadataComponent: AddDocMetadataComponent;

  docName: string;
  product: Product;
  catalog: RevCatalog | null = null;
  catalogList: RevCatalog[] = [];
  isUploading: boolean;
  isValidFile: boolean;
  docS3Key: string;
  docFileName: string;
  fileName: string;
  saving: boolean;
  assetType: DataElement;
  validationFileErrorMessage = "";
  fileValidTypes: string;

  newCatSystemVersion = newCatSystemVersion;

  get productList(): Product[] {
    return this.ngRedux.getState().productList;
  }

  get assetTypes(): DataElement[] {
    if (!this.catalog) return [];
    return this.catalog.catAssetTypeList
      .sort((a, b) => {
        if (a.label > b.label) return 1;
        if (a.label < b.label) return -1;
        return 0;
      });
  }

  get enableSubmit(): boolean {
    return (
      this.docName != null &&
      this.catalog != null &&
      this.assetType != null &&
      this.docFileName != null &&
      (this.addDocMetadataComponent == null || this.addDocMetadataComponent.canSubmit())
    );
  }

  constructor(
    private ngRedux: NgRedux<AppState>,
    private revService: RevService,
    private uploadService: UploadService,
    private authService: AuthService,
    private sharedService: SharedService,
    private catService: CatService,
    private baseDocMetadataHandler: DocMetadataHandler,
    private versionDocMetadataHandler: VersionDocMetadataHandler
  ) {}

  // "Add version" (identified by an incoming parentDoc) uses the version-aware handler;
  // "Add document" uses the base handler.
  public docMetadataHandler!: DocMetadataHandler;

  ngOnInit(): void {
    this.docMetadataHandler = this.parentDoc
      ? this.versionDocMetadataHandler
      : this.baseDocMetadataHandler;

    if (this.parentDoc) {
      this.docName = this.parentDoc.docName;
      this.product = this.productList.find(
        (p) => p.id == this.parentDoc.productId
      );
      // Force the same document metadata across the version tree: preset from the parent
      // version and render it read-only.
      this.versionDocMetadataHandler.parentDocId = this.parentDoc.docId;
    }
    this.fileValidTypes = this.uploadService.fileValidTypes;
    this.getCatalogList();
  }

  close() {
    this.onClose.emit();
  }

  getCatalogList() {
    this.catalogList = [];
    this.catalogList.loading = true;
    this.catalog = null;

    this.revService.getRevCatalogList(this.product).subscribe(
      (data) => {
        this.catalogList = data;
      },
      (err) => (this.catalogList.loading = false)
    );
  }

  uploadFile($event) {
    const fileData: File = $event.target.files[0];
    if (!fileData) return;
    this.isValidFile = undefined;
    this.docFileName = null;
    this.fileName = null;

    this.isValidFile = this.uploadService.validateFile(
      fileData,
      this.fileValidTypes
      //SharedService.revDocFileTypes
    );
    if (!this.isValidFile) {
      
      this.validationFileErrorMessage = this.uploadService.fileInvalidTypeMessage;
      return;

    }

    this.isUploading = true;

    const file: FileUpload = new FileUpload();
    file.fileType = this.uploadService.getFileType(fileData);
    this.fileName = fileData.name;

    (async () => {
      await this.uploadService.uploadFile(fileData);
      file.file = fileData;

      this.docFileName = null;

      this.uploadService
        .getPresignedUrl(file, this.authService.getPresignedUrl)
        .subscribe(
          (data) => {
            this.uploadService.putFile(file, data).subscribe(
              (res) => {
                
                if (file.fileType === '.pdf') {
                  let fileValidationParams: FileValidationParams = new FileValidationParams();
                  fileValidationParams.s3_key = data.s3Key;
                  fileValidationParams.env_id = this.ngRedux.getState().curEnv.id;
                  fileValidationParams.source_bucket = this.ngRedux.getState().curEnv.tmpS3Bucket;
                  this.uploadService.validateFileAdditionally(fileValidationParams).subscribe((fileValidationResponse: FileValidationResponse) => {
                    if (!fileValidationResponse.is_valid) {

                      this.isValidFile = false;
                      this.validationFileErrorMessage = fileValidationResponse.error_message.join('');

                    } else {

                      this.docS3Key = data.s3Key;
                      this.docFileName = file.file.name;
                    } 
                    
                    this.isUploading = false;
                    
                  })
              } else {
                    this.docS3Key = data.s3Key;
                    this.docFileName = file.file.name;
                    this.isUploading = false;
              }
              },
              (err) => {
                this.sharedService.showInfoMessage({infoType: 'error', messageKey: err,  showToast: false})
                this.isUploading = false;
              }
            );
          },
          (err) => {
            this.sharedService.showInfoMessage({infoType: 'error', messageKey: err,  showToast: false})
            this.isUploading = false;
          }
        );
    })();

    $event.target.value = '';
  }

  removeFile() {
    this.docFileName = null;
    this.fileName = null;
  }

  createDoc() {
    this.saving = true;

    let doc = new RevDoc();
    doc.docName = this.docName;
    if (this.parentDoc) {
      doc.parentDocId = this.parentDoc.parentDocId;
    }
    doc.docName = this.docName;
    doc.catId = this.catalog.catId;
    doc.catVersionId = this.catalog.catVersionId;
    doc.docS3Key = this.docS3Key;
    doc.fileName = this.docFileName;
    doc.assetType = this.assetType;
    const metadata = this.docMetadataHandler.getDocMetadataToAdd();

    if (metadata != null) {
      doc.metadata = metadata;
    }
    var encryptSw = !doc.fileName.toLocaleLowerCase().endsWith('.zip');

    this.revService.createRevDoc(doc).subscribe(
      (savedDoc) => {
        if (encryptSw) {
          this.uploadService
            .encryptFile(
              savedDoc.docId,
              'REV',
              this.ngRedux.getState().curEnv.tmpS3Bucket,
              this.ngRedux.getState().curEnv.docS3Bucket,
              doc.docS3Key
            )
            .subscribe(
              (res) => {
                this.catService
                  .processIndDocAlg(savedDoc.runNum)
                  .subscribe((data) => {});
                this.saving = false;
                this.onDocCreated.emit(savedDoc);
              },
              (err) => {
                this.sharedService.showInfoMessage({infoType: 'error', messageKey: err,  showToast: false})
                this.saving = false;
              }
            );
        } else {
          this.catService
            .processIndDocAlg(savedDoc.runNum)
            .subscribe((data) => {});
          this.saving = false;
          this.onDocCreated.emit(savedDoc);
        }
      },

      (err) => (this.saving = false)
    );
  }
  get envId(): number {

    return this.ngRedux.getState().curEnv.id;
  }

  get lang() {
    return this.ngRedux.getState().ui_lang
  }
  get catVersionId() {
    return this.catalog?.catVersionId;
  }

  get catSystemVersion() {
    
    return this.catalog?.systemVersion;

  }
}
